<script setup lang="ts">
  import { MpCard, MpContextMenu, MpDivider, MpText, useMpScreen } from '@borg/ui';

  const props = withDefaults(
    defineProps<{
      title?: string;
      preview?: boolean;
      isEditable?: boolean;
      isDeletable?: boolean;
    }>(),
    {
      title: undefined,
      isEditable: false,
      isDeletable: false,
    },
  );

  const i18n = useI18n();
  const { isMobile } = useMpScreen();

  const emits = defineEmits<{
    (eventName: 'edit'): void;
    (eventName: 'delete'): void;
  }>();

  const options = computed(() => {
    const options = [];
    if (props.isEditable) {
      options.push({ label: i18n.t('EDIT'), onClick: () => emits('edit') });
    }
    if (props.isDeletable) {
      options.push({ label: i18n.t('DELETE'), onClick: () => emits('delete') });
    }
    return options;
  });
</script>

<template>
  <MpCard
    :class="{
      'my-profile-section': true,
      'my-profile-section--preview': preview,
    }"
    :p="preview ? 'unset' : isMobile ? 'sm' : 'lg'"
    :border="!preview"
    :shadow="!preview"
    :corners="preview ? 'square' : 'round-md'"
  >
    <div class="my-profile-section__header">
      <slot name="title">
        <MpText
          v-if="title"
          as="h5"
          class="my-profile-section__title"
          no-margin
          uppercase
        >
          {{ title }}
        </MpText>
      </slot>
      <div
        v-if="!preview && options.length"
        class="my-profile-section__options"
      >
        <MpContextMenu
          :test="`${title}-context-menu`"
          :options="options"
        />
      </div>
    </div>
    <MpDivider v-if="title && !preview" />
    <div class="my-profile-section__content">
      <slot />
    </div>
  </MpCard>
</template>

<style scoped lang="scss">
  .my-profile-section {
    $self: &;

    margin-bottom: var(--mp-space-70);

    &__header {
      display: flex;
      align-items: center;
    }

    &__title {
      flex: 2;
    }

    &--preview {
      display: flex;
      gap: var(--mp-space-60);
      flex-direction: row;
      border-bottom: 1px solid var(--mp-color-grey-200);
      padding-bottom: var(--mp-space-70) !important;

      #{$self}__header {
        width: 200px;
        margin-bottom: 0 !important;
        align-items: flex-start;
      }

      #{$self}__content {
        flex: 4;
      }
    }

    &:last-of-type {
      margin-bottom: 0;
    }

    &--preview:last-of-type {
      border-bottom: 0;
      padding-bottom: 0 !important;
    }
  }
</style>
